/**
 * Intelligence API hooks — timeline, risk scoring, global search,
 * community detection, entity comparison, alert rules, PDF export.
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import apiClient from './client';

// ── Timeline ──────────────────────────────────────────────────────────────

export interface TimelineEvent {
  id: string;
  event_type: string;
  title: string;
  description: string;
  severity: string;
  metadata: Record<string, unknown>;
  source: string;
  created_at: string;
}

export interface TimelineResponse {
  entity_id: string;
  entity_name: string;
  events: TimelineEvent[];
  total: number;
  page: number;
  page_size: number;
}

async function fetchTimeline(entityId: string, days = 90, page = 1): Promise<TimelineResponse> {
  const { data } = await apiClient.get(`/timeline/${entityId}`, { params: { days, page, page_size: 50 } });
  return data;
}

export function useTimeline(entityId: string | null, days = 90) {
  return useQuery({
    queryKey: ['timeline', entityId, days],
    queryFn: () => fetchTimeline(entityId!, days),
    enabled: !!entityId,
    staleTime: 30_000,
  });
}

// ── Risk Scoring ──────────────────────────────────────────────────────────

export interface RiskFactor {
  name: string;
  score: number;
  weight: number;
  details: string;
}

export interface RiskScoreResponse {
  entity_id: string;
  entity_name: string;
  risk_score: number;
  risk_level: string;
  factors: RiskFactor[];
  computed_at: string;
}

async function fetchRiskScore(entityId: string): Promise<RiskScoreResponse> {
  const { data } = await apiClient.get(`/risk/${entityId}`);
  return data;
}

async function fetchTopRisks(limit = 20, minScore = 0.3): Promise<{ items: Array<{ id: string; name: string; entity_type: string; risk_score: number }> }> {
  const { data } = await apiClient.get('/risk/top', { params: { limit, min_score: minScore } });
  return data;
}

export function useRiskScore(entityId: string | null) {
  return useQuery({
    queryKey: ['risk-score', entityId],
    queryFn: () => fetchRiskScore(entityId!),
    enabled: !!entityId,
    staleTime: 60_000,
  });
}

export function useTopRisks(limit = 20, minScore = 0.3) {
  return useQuery({
    queryKey: ['top-risks', limit, minScore],
    queryFn: () => fetchTopRisks(limit, minScore),
    staleTime: 30_000,
  });
}

export function useBatchRiskScore() {
  return useMutation({
    mutationFn: async (params: { entity_ids?: string[]; entity_type?: string }) => {
      const { data } = await apiClient.post('/risk/batch', params);
      return data;
    },
  });
}

// ── Global Search ─────────────────────────────────────────────────────────

export interface SearchResult {
  id: string;
  module: string;
  title: string;
  snippet: string;
  relevance: number;
  entity_type: string | null;
  url: string | null;
  created_at: string | null;
}

export interface GlobalSearchResponse {
  query: string;
  results: SearchResult[];
  total: number;
  modules_searched: string[];
  search_time_ms: number;
}

async function globalSearch(query: string, modules: string[] = []): Promise<GlobalSearchResponse> {
  const { data } = await apiClient.post('/search/global', { query, modules, limit_per_module: 15 });
  return data;
}

export function useGlobalSearch(query: string, modules: string[] = []) {
  return useQuery({
    queryKey: ['global-search', query, modules],
    queryFn: () => globalSearch(query, modules),
    enabled: query.length >= 2,
    staleTime: 15_000,
  });
}

// ── Communities ────────────────────────────────────────────────────────────

export interface CommunityMember {
  entity_id: string;
  entity_name: string;
  entity_type: string;
  degree: number;
}

export interface Community {
  id: number;
  size: number;
  members: CommunityMember[];
  density: number;
  hub_entity: CommunityMember | null;
  entity_types: Record<string, number>;
}

export interface CommunityDetectResponse {
  algorithm: string;
  communities: Community[];
  total_communities: number;
  total_entities: number;
  total_relationships: number;
  isolated_entities: number;
  computed_at: string;
}

export function useCommunities(algorithm = 'label_propagation', minSize = 2) {
  return useQuery({
    queryKey: ['communities', algorithm, minSize],
    queryFn: async () => {
      const { data } = await apiClient.post('/communities/detect', {
        algorithm,
        min_community_size: minSize,
        max_communities: 50,
      });
      return data as CommunityDetectResponse;
    },
    staleTime: 120_000,
  });
}

// ── Entity Comparison ─────────────────────────────────────────────────────

export interface CompareResponse {
  entities: Array<{
    id: string;
    name: string;
    entity_type: string;
    properties: Record<string, unknown>;
    relationship_count: number;
    risk_score: number | null;
  }>;
  common_connections: Array<Record<string, unknown>>;
  shared_properties: Array<{ key: string; value: unknown; entity_ids: string[] }>;
  differences: Array<{ key: string; values: Record<string, unknown> }>;
  similarity_score: number;
}

export function useCompareEntities(entityIds: string[]) {
  return useQuery({
    queryKey: ['compare', entityIds],
    queryFn: async () => {
      const { data } = await apiClient.post('/compare', { entity_ids: entityIds });
      return data as CompareResponse;
    },
    enabled: entityIds.length >= 2,
    staleTime: 60_000,
  });
}

// ── Alert Rules ───────────────────────────────────────────────────────────

export interface AlertRule {
  id: string;
  name: string;
  description: string;
  rule_type: string;
  conditions: Record<string, unknown>;
  severity: string;
  notify_email: string | null;
  active: boolean;
  created_by: string;
  created_at: string;
  last_triggered_at: string | null;
  trigger_count: number;
}

export function useAlertRules(active?: boolean) {
  return useQuery({
    queryKey: ['alert-rules', active],
    queryFn: async () => {
      const params: Record<string, unknown> = { page_size: 100 };
      if (active !== undefined) params.active = active;
      const { data } = await apiClient.get('/alert-rules', { params });
      return data as { items: AlertRule[]; total: number };
    },
    staleTime: 30_000,
  });
}

export function useCreateAlertRule() {
  return useMutation({
    mutationFn: async (rule: Omit<AlertRule, 'id' | 'created_by' | 'created_at' | 'last_triggered_at' | 'trigger_count'>) => {
      const { data } = await apiClient.post('/alert-rules', rule);
      return data;
    },
  });
}

// ── PDF Export ─────────────────────────────────────────────────────────────

export async function exportEntityPDF(entityId: string): Promise<void> {
  const { data } = await apiClient.post(`/export/pdf/${entityId}`, null, { responseType: 'blob' });
  const blob = new Blob([data], { type: 'text/html' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `helios-report-${entityId}.html`;
  a.click();
  window.URL.revokeObjectURL(url);
}

// ── Dashboard Stats ──────────────────────────────────────────────────────

export interface DashboardStats {
  total_entities: number;
  entities_by_type: Record<string, number>;
  total_relationships: number;
  new_entities_24h: number;
  new_relationships_24h: number;
  avg_risk_score: number;
  high_risk_count: number;
  critical_alerts_count: number;
}

export interface TrendPoint { date: string; count: number; }
export interface RiskTrend { date: string; avg_score: number; }
export interface DashboardTrends {
  entities_per_day: TrendPoint[];
  relationships_per_day: TrendPoint[];
  risk_score_trend: RiskTrend[];
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => { const { data } = await apiClient.get('/dashboard/stats'); return data as DashboardStats; },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useDashboardTrends() {
  return useQuery({
    queryKey: ['dashboard-trends'],
    queryFn: async () => { const { data } = await apiClient.get('/dashboard/trends'); return data as DashboardTrends; },
    staleTime: 60_000,
  });
}

export function useTopEntities() {
  return useQuery({
    queryKey: ['dashboard-top-entities'],
    queryFn: async () => { const { data } = await apiClient.get('/dashboard/top-entities'); return data; },
    staleTime: 30_000,
  });
}

export function useActivityFeed() {
  return useQuery({
    queryKey: ['dashboard-activity'],
    queryFn: async () => { const { data } = await apiClient.get('/dashboard/activity'); return data as Array<{ action: string; user_id: string; resource_type: string; created_at: string }>; },
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

// ── Deduplication ────────────────────────────────────────────────────────

export interface DuplicateGroup {
  entity_a: { id: string; name: string; entity_type: string };
  entity_b: { id: string; name: string; entity_type: string };
  similarity: number;
  matching_fields: string[];
}

export function useDedupScan(entityType?: string, minSimilarity = 0.7) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/dedup/scan', { entity_type: entityType, min_similarity: minSimilarity });
      // Backend returns { pairs, total } — map to frontend shape
      return { duplicates: data.pairs || data.duplicates || [], total_scanned: data.total ?? data.total_scanned ?? 0 };
    },
  });
}

export function useDedupMerge() {
  return useMutation({
    mutationFn: async (params: { primary_id: string; secondary_id: string }) => {
      const { data } = await apiClient.post('/dedup/merge', params);
      return data;
    },
  });
}

// ── Annotations ──────────────────────────────────────────────────────────

export interface Annotation {
  id: string;
  entity_id: string;
  user_id: string;
  content: string;
  tags: string[];
  annotation_type: string;
  created_at: string;
}

export function useAnnotations(entityId: string | null) {
  return useQuery({
    queryKey: ['annotations', entityId],
    queryFn: async () => { const { data } = await apiClient.get(`/annotations/${entityId}`); return data as Annotation[]; },
    enabled: !!entityId,
    staleTime: 15_000,
  });
}

export function useCreateAnnotation() {
  return useMutation({
    mutationFn: async (params: { entity_id: string; content: string; tags: string[]; annotation_type: string }) => {
      const { data } = await apiClient.post(`/annotations/${params.entity_id}`, params);
      return data;
    },
  });
}

// ── Anomaly Detection ────────────────────────────────────────────────────

export interface Anomaly {
  entity_id: string;
  entity_name: string;
  anomaly_type: string;
  severity: string;
  score: number;
  details: string;
  detected_at: string;
}

export function useAnomalyDetect() {
  return useMutation({
    mutationFn: async (entityType?: string) => {
      const { data } = await apiClient.post('/anomaly/detect', { entity_type: entityType });
      return { anomalies: data.anomalies || [], total_analyzed: data.total ?? data.total_analyzed ?? 0 };
    },
  });
}

export function useAnomalyHistory() {
  return useQuery({
    queryKey: ['anomaly-history'],
    queryFn: async () => { const { data } = await apiClient.get('/anomaly/history'); return (data.items || data) as Anomaly[]; },
    staleTime: 60_000,
  });
}

// ── Investigation Workflows ──────────────────────────────────────────────

export interface Workflow {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  assigned_to: string | null;
  entity_ids: string[];
  tags: string[];
  steps: Array<{ title: string; description: string; step_type: string; completed: boolean }>;
  created_by: string;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

export function useWorkflows(status?: string) {
  return useQuery({
    queryKey: ['workflows', status],
    queryFn: async () => {
      const params: Record<string, unknown> = { page_size: 50 };
      if (status) params.status = status;
      const { data } = await apiClient.get('/workflows', { params });
      return data as { items: Workflow[]; total: number };
    },
    staleTime: 15_000,
  });
}

export function useWorkflowKanban() {
  return useQuery({
    queryKey: ['workflows-kanban'],
    queryFn: async () => {
      const { data } = await apiClient.get('/workflows/kanban');
      // Backend returns { columns: [{status, workflows, count}] } — transform to Record<string, Workflow[]>
      if (data.columns) {
        const map: Record<string, Workflow[]> = {};
        for (const col of data.columns) map[col.status] = col.workflows;
        return map;
      }
      return data as Record<string, Workflow[]>;
    },
    staleTime: 15_000,
  });
}

export function useCreateWorkflow() {
  return useMutation({
    mutationFn: async (params: { title: string; description: string; priority: string; entity_ids: string[]; tags: string[] }) => {
      const { data } = await apiClient.post('/workflows', params);
      return data as Workflow;
    },
  });
}

export function useTransitionWorkflow() {
  return useMutation({
    mutationFn: async (params: { workflow_id: string; new_status: string }) => {
      const { data } = await apiClient.post(`/workflows/${params.workflow_id}/transition`, { new_status: params.new_status });
      return data;
    },
  });
}

// ── Audit Trail ──────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  created_at: string;
}

export function useAuditLog(filters?: { user_id?: string; action?: string; date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['audit-log', filters],
    queryFn: async () => {
      const { data } = await apiClient.get('/audit', { params: filters });
      return data as { items: AuditEntry[]; total: number };
    },
    staleTime: 15_000,
  });
}

export function useAuditStats() {
  return useQuery({
    queryKey: ['audit-stats'],
    queryFn: async () => { const { data } = await apiClient.get('/audit/stats'); return data; },
    staleTime: 60_000,
  });
}

// ── Geo Intelligence ─────────────────────────────────────────────────────

export interface GeoEntity {
  id: string;
  name: string;
  entity_type: string;
  lat: number;
  lng: number;
  risk_score: number;
}

export function useGeoEntities(entityType?: string, minRisk?: number) {
  return useQuery({
    queryKey: ['geo-entities', entityType, minRisk],
    queryFn: async () => {
      const params: Record<string, unknown> = {};
      if (entityType) params.entity_type = entityType;
      if (minRisk !== undefined) params.min_risk_score = minRisk;
      const { data } = await apiClient.get('/geo/entities', { params });
      return (data.items || data) as GeoEntity[];
    },
    staleTime: 60_000,
  });
}

export function useGeoHeatmap() {
  return useQuery({
    queryKey: ['geo-heatmap'],
    queryFn: async () => { const { data } = await apiClient.get('/geo/heatmap'); return (data.points || data) as Array<{ lat: number; lng: number; count: number; avg_risk: number }>; },
    staleTime: 120_000,
  });
}

// ── Enrichment ───────────────────────────────────────────────────────────

export function useEnrichEntity() {
  return useMutation({
    mutationFn: async (params: { entity_id: string; sources?: string[] }) => {
      const { data } = await apiClient.post(`/enrichment/enrich/${params.entity_id}`, { sources: params.sources });
      return data;
    },
  });
}

export function useEnrichmentTasks(entityId: string | null) {
  return useQuery({
    queryKey: ['enrichment-tasks', entityId],
    queryFn: async () => { const { data } = await apiClient.get(`/enrichment/tasks/${entityId}`); return data; },
    enabled: !!entityId,
    staleTime: 10_000,
  });
}

// ── Shortest Path / Graph Analysis ──────────────────────────────────────

export interface PathResult {
  path: Array<{ entity_id: string; name: string; entity_type: string }>;
  edges: Array<{ source: string; target: string; relation_type: string }>;
  depth: number;
  found: boolean;
}

export function useShortestPath(sourceId: string | null, targetId: string | null, maxDepth = 6) {
  return useQuery({
    queryKey: ['shortest-path', sourceId, targetId, maxDepth],
    queryFn: async () => {
      const { data } = await apiClient.get(`/graph/path/shortest/${sourceId}/${targetId}`, { params: { max_depth: maxDepth } });
      return data as PathResult;
    },
    enabled: !!sourceId && !!targetId && sourceId !== targetId,
    staleTime: 30_000,
  });
}

export function useDegreeSeparation(entityId: string | null) {
  return useQuery({
    queryKey: ['degree-separation', entityId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/graph/path/degrees/${entityId}`);
      return data as { direct: number; second_degree: number; third_degree: number };
    },
    enabled: !!entityId,
    staleTime: 60_000,
  });
}

export function useCommonConnections(entityA: string | null, entityB: string | null) {
  return useQuery({
    queryKey: ['common-connections', entityA, entityB],
    queryFn: async () => {
      const { data } = await apiClient.get(`/graph/path/common-connections/${entityA}/${entityB}`);
      return data as Array<{ id: string; name: string; entity_type: string }>;
    },
    enabled: !!entityA && !!entityB && entityA !== entityB,
    staleTime: 30_000,
  });
}

// ── Auto Risk Scoring ───────────────────────────────────────────────────

export function useAutoRiskRecalculate() {
  return useMutation({
    mutationFn: async (entityId?: string) => {
      const url = entityId ? `/risk/auto/recalculate/${entityId}` : '/risk/auto/recalculate';
      const { data } = await apiClient.post(url);
      return data;
    },
  });
}

export function useRiskFactors(entityId: string | null) {
  return useQuery({
    queryKey: ['risk-factors', entityId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/risk/auto/factors/${entityId}`);
      return data as { connection_score: number; contagion_score: number; anomaly_score: number; flag_score: number; total: number; factors: Array<{ name: string; value: number; description: string }> };
    },
    enabled: !!entityId,
    staleTime: 60_000,
  });
}

// ── Bulk Import ─────────────────────────────────────────────────────────

export function useBulkImportCSV() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data as { imported: number; errors: Array<{ row: number; error: string }>; total_rows: number };
    },
  });
}

export function useBulkImportRelCSV() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/import/relationships/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}

export function useBulkImportJSON() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/import/json', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}

// ── Webhooks ────────────────────────────────────────────────────────────

export interface WebhookConfig {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  created_at: string;
  last_triggered_at: string | null;
}

export function useWebhooks() {
  return useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => { const { data } = await apiClient.get('/webhooks'); return (data.items || data) as WebhookConfig[]; },
    staleTime: 30_000,
  });
}

export function useCreateWebhook() {
  return useMutation({
    mutationFn: async (params: { name: string; url: string; events: string[]; secret?: string }) => {
      const { data } = await apiClient.post('/webhooks', params);
      return data as WebhookConfig;
    },
  });
}

export function useDeleteWebhook() {
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/webhooks/${id}`);
    },
  });
}

export function useTestWebhook() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(`/webhooks/${id}/test`);
      return data;
    },
  });
}

// ── Backup ──────────────────────────────────────────────────────────────

export function useBackupExport() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post('/backup/export', {}, { responseType: 'blob' });
      return data;
    },
  });
}

export function useBackupImport() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/backup/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  });
}

export function useBackupStatus() {
  return useQuery({
    queryKey: ['backup-status'],
    queryFn: async () => {
      const { data } = await apiClient.get('/backup/status');
      return data as { entity_count: number; relationship_count: number; table_sizes: Record<string, string> };
    },
    staleTime: 60_000,
  });
}

// ── RBAC ────────────────────────────────────────────────────────────────

export interface RolePermissions {
  role: string;
  permissions: Record<string, string>;
}

export function useRoles() {
  return useQuery({
    queryKey: ['rbac-roles'],
    queryFn: async () => { const { data } = await apiClient.get('/rbac/roles'); return (data.roles || data) as RolePermissions[]; },
    staleTime: 60_000,
  });
}

export function useUpdateRole() {
  return useMutation({
    mutationFn: async (params: { role: string; permissions: Record<string, string> }) => {
      const { data } = await apiClient.put(`/rbac/roles/${params.role}`, params.permissions);
      return data;
    },
  });
}

export function useCreateRole() {
  return useMutation({
    mutationFn: async (params: { role: string; permissions: Record<string, string> }) => {
      const { data } = await apiClient.post('/rbac/roles', params);
      return data;
    },
  });
}

export function useCheckPermission(module: string) {
  return useQuery({
    queryKey: ['rbac-check', module],
    queryFn: async () => {
      const { data } = await apiClient.get('/rbac/check', { params: { module } });
      return data as { allowed: boolean; level: string };
    },
    staleTime: 120_000,
  });
}
